<template>
    <div>
        <breadcrumbs class="mb-3" />
        <page-title :text="$t('page_titles.vehicle_types_page_title')" />
        <ModeSwitcher ref="modeSwitcher" :main-mode-text="$t('create')" :create-mode-text="$t('create_vehicle_type')">
            <template v-slot:main-mode>
                <div>
                    <VehicleTypesFilter
                        v-model="filters"
                        :has-active-filters="hasActiveFilters"
                        @submit="loadData()"
                        @reset="resetFilters"
                    />
                    <v-data-table-server
                        :headers="useHeaders"
                        :items="vehicleTypes"
                        :items-per-page-options="useTableOptions(totalCount)"
                        :options="tableOptions"
                        :loading="isLoading"
                        :items-length="totalCount"
                        disable-sort
                        density="compact"
                        color="primary"
                        class="elevation-1 table-server"
                        data-cy="vehicle-types-table"
                        @update:options="onUpdateTableOptions"
                    >
                        <template v-slot:item.name="{item}">
                            <nuxt-link
                                no-prefetch
                                :to="{name: 'vehicleType', params: {id: item.id}}"
                                :data-cy="`vehicleT-type-${item.id}`"
                            >
                                {{ item.name }}
                            </nuxt-link>
                        </template>
                        <template v-slot:item.active="{item}">
                            {{ item.active ? t('active') : t('inactive') }}
                        </template>
                    </v-data-table-server>
                </div>
            </template>
            <template v-slot:additional-mode>
                <v-row dense class="mb-5">
                    <v-col cols="12" md="5">
                        <VehicleTypeForm
                            :edit-vehicle-type-id="editVehicleTypeId"
                            @save="save()"
                            @cancel="modeSwitcher.goToTable()"
                        />
                    </v-col>
                </v-row>
            </template>
        </ModeSwitcher>
    </div>
</template>
<script setup lang="ts">
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import {useTableOptions} from '~/composables/helpers'
    import VehicleTypesFilter from '~/components/account/vehicleTypes/VehicleTypesFilter.vue'
    import VehicleTypeForm from '~/components/account/vehicleTypes/VehicleTypeForm.vue'
    import PageTitle from '~/components/parts/PageTitle.vue'

    const initVehicleTypesFilter = () => ({
        name: null,
        active: true,
    })

    const isLoading = ref<boolean>(false)
    const vehicleTypes = ref<any[]>([])
    const totalCount = ref<number>(0)
    const tableOptions = ref({
        itemsPerPage: 10,
        page: 1,
    })
    const filters = ref(initVehicleTypesFilter())
    const modeSwitcher = ref(null)
    const editVehicleTypeId = ref<number>(null)

    const app = useNuxtApp()
    const {t} = useI18n()

    async function loadData(): Promise<void> {
        const {itemsPerPage, page} = tableOptions.value

        try {
            isLoading.value = true
            const rq = {
                ...filters.value,
                ...(filters.value.name && {pattern: filters.value.name}),
            }
            clean(rq)
            if (itemsPerPage !== -1) Object.assign(rq, {limit: itemsPerPage, offset: itemsPerPage * (page - 1)})

            const res = await app.$api.vehicleType.get(rq)
            totalCount.value = res.count
            vehicleTypes.value = res.vehicleTypes
        } catch (e) {
            vehicleTypes.value = []
        } finally {
            isLoading.value = false
        }
    }

    const save = async () => {
        await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders = computed(() => {
        return [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('capacity'), value: 'maxCapacity'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
    })

    const hasActiveFilters = computed(() => {
        return !isEqual(initVehicleTypesFilter(), filters.value)
    })

    const resetFilters = (): void => {
        filters.value = initVehicleTypesFilter()
        loadData()
    }

    const onUpdateTableOptions = (options): void => {
        tableOptions.value = {itemsPerPage: options.itemsPerPage, page: options.page}
        loadData()
    }
</script>
